import { useState, useEffect } from "react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import api, { formatDate } from "../lib/api";
import { toast } from "sonner";
import { 
  Users,
  Mail,
  Phone,
  MessageCircle,
  Filter,
  Download,
  RefreshCw
} from "lucide-react";

const LeadsPage = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const response = await api.get("/leads");
      setLeads(response.data);
    } catch (error) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  const updateLeadStatus = async (leadId, status) => {
    try {
      await api.put(`/leads/${leadId}/status?status=${status}`);
      toast.success("Lead status updated");
      fetchLeads();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const exportLeads = () => {
    const csv = leads.map(lead => 
      `${lead.name || ''},${lead.email},${lead.phone || ''},${lead.type},${lead.status},${lead.created_at}`
    ).join('\n');
    
    const header = 'Name,Email,Phone,Type,Status,Created\n';
    const blob = new Blob([header + csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success("Leads exported");
  };

  const filteredLeads = filter === "all" 
    ? leads 
    : leads.filter(l => l.type === filter || l.status === filter);

  const stats = {
    total: leads.length,
    newsletter: leads.filter(l => l.type === "newsletter").length,
    contact: leads.filter(l => l.type === "contact").length,
    new: leads.filter(l => l.status === "new").length
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "new": return <Badge className="bg-blue-500/20 text-blue-400">New</Badge>;
      case "contacted": return <Badge className="bg-yellow-500/20 text-yellow-400">Contacted</Badge>;
      case "qualified": return <Badge className="bg-emerald/20 text-emerald">Qualified</Badge>;
      case "converted": return <Badge className="bg-purple-500/20 text-purple-400">Converted</Badge>;
      case "active": return <Badge className="bg-emerald/20 text-emerald">Active</Badge>;
      default: return <Badge className="bg-gray-500/20 text-gray-400">{status}</Badge>;
    }
  };

  return (
    <DashboardLayout 
      title="Lead Management" 
      subtitle="Track and manage all leads from your website"
    >
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-surface border-platinum/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-platinum/60 text-sm">Total Leads</p>
                <p className="text-2xl font-semibold text-slate-900">{stats.total}</p>
              </div>
              <Users className="text-burgundy" size={24} />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-surface border-platinum/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-platinum/60 text-sm">Newsletter</p>
                <p className="text-2xl font-semibold text-slate-900">{stats.newsletter}</p>
              </div>
              <Mail className="text-burgundy" size={24} />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-surface border-platinum/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-platinum/60 text-sm">Contact Forms</p>
                <p className="text-2xl font-semibold text-slate-900">{stats.contact}</p>
              </div>
              <MessageCircle className="text-burgundy" size={24} />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-surface border-platinum/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-platinum/60 text-sm">New (Uncontacted)</p>
                <p className="text-2xl font-semibold text-slate-900">{stats.new}</p>
              </div>
              <Filter className="text-burgundy" size={24} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40 bg-surface border-platinum/20">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent className="bg-surface border-platinum/10">
              <SelectItem value="all">All Leads</SelectItem>
              <SelectItem value="newsletter">Newsletter</SelectItem>
              <SelectItem value="contact">Contact Form</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="qualified">Qualified</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchLeads} className="border-platinum/20">
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </Button>
          <Button onClick={exportLeads} className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm">
            <Download size={16} className="mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Leads Table */}
      <Card className="bg-surface border-platinum/10">
        <CardHeader>
          <CardTitle className="text-white font-serif">All Leads</CardTitle>
          <CardDescription>Click on a lead to view details and update status</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-platinum/10">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Date</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Name</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Email</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Phone</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Type</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Status</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map((lead) => (
                  <tr key={lead.id} className="border-b border-platinum/10 hover:bg-white/5">
                    <td className="py-3 px-4 text-gray-400 text-sm">{formatDate(lead.created_at)}</td>
                    <td className="py-3 px-4 text-white">{lead.name || '-'}</td>
                    <td className="py-3 px-4">
                      <a href={`mailto:${lead.email}`} className="text-burgundy hover:underline">
                        {lead.email}
                      </a>
                    </td>
                    <td className="py-3 px-4 text-gray-400">
                      {lead.phone ? (
                        <a href={`tel:${lead.phone}`} className="hover:text-white">
                          {lead.phone}
                        </a>
                      ) : '-'}
                    </td>
                    <td className="py-3 px-4">
                      <Badge className={lead.type === "newsletter" ? "bg-blue-500/20 text-blue-400" : "bg-purple-500/20 text-purple-400"}>
                        {lead.type}
                      </Badge>
                    </td>
                    <td className="py-3 px-4">{getStatusBadge(lead.status)}</td>
                    <td className="py-3 px-4 text-right">
                      <Select 
                        value={lead.status} 
                        onValueChange={(v) => updateLeadStatus(lead.id, v)}
                      >
                        <SelectTrigger className="w-32 h-8 bg-surface-highlight border-platinum/20 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-surface border-platinum/10">
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="qualified">Qualified</SelectItem>
                          <SelectItem value="converted">Converted</SelectItem>
                          <SelectItem value="unsubscribed">Unsubscribed</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}
                {filteredLeads.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-gray-500">
                      {loading ? "Loading..." : "No leads found"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Lead Details Modal could go here */}
    </DashboardLayout>
  );
};

export default LeadsPage;
